import React, { useState, useRef, useEffect } from "react";
import { FiChevronUp, FiChevronDown } from "react-icons/fi";
import CustomTooltip from "./CustomTooltip";
import { useOverflowTooltip } from "../hooks/useOverflowTooltip";
import "./CustomSelect.css";

const OverflowTooltipLabel = ({
  text,
  placement = "top",
  color = "#4A1476",
  maxWidth = "260px",
  enableTooltip = true,
}) => {
  const { ref, showTooltip } = useOverflowTooltip(text);
  const label = <span ref={ref} className="custom-select-label-text">{text}</span>;

  if (!enableTooltip || !showTooltip) return label;

  return (
    <CustomTooltip
      message={String(text ?? "")}
      placement={placement}
      color={color}
      maxWidth={maxWidth}
      autoAdjustOverflow
    >
      {label}
    </CustomTooltip>
  );
};

const toSearchableText = (node) => {
  if (node === null || node === undefined || typeof node === "boolean") {
    return "";
  }
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(toSearchableText).join(" ");
  }
  if (React.isValidElement(node)) {
    return toSearchableText(node.props?.children);
  }
  return "";
};

const CustomSelect = ({
  value,
  onChange,
  options = [],
  width = "100%",
  placeholder = "Select",
  disabled = false,
  disableSearch = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);
  const SEARCH_THRESHOLD = 5;
  // Normalize options
  const normalizedOptions = Array.from(
  new Map(
    options.map((opt) => {
      const o =
        typeof opt === "string"
          ? { value: opt, label: opt }
          : opt;
      return [o.value, o];
    })
  ).values()
);

  // const normalizedOptions = options.map((opt) =>
  //   typeof opt === "string" ? { value: opt, label: opt } : opt
  // );

// Filter options
const filteredOptions = normalizedOptions.filter((opt) => {
  const label = String(
    opt.searchText ??
    toSearchableText(opt.label) ??
    String(opt.value ?? "")
  ).toLowerCase();
  const term = searchTerm.toLowerCase();

  return label.includes(term);
});



  const selectedOption = normalizedOptions.find((o) => o.value === value);
  const selectedLabel = selectedOption?.label || placeholder;
  const hasSelectedValue = selectedOption !== undefined;

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      className="custom-select-container"
      style={{ width }}
      ref={containerRef}
    >
      {/* HEADER */}
      <div
        className={`custom-select-header ${disabled ? "disabled" : ""}`}
        onClick={() => !disabled && setOpen((p) => !p)}
      >
        <span
          className={
            hasSelectedValue ? "custom-select-value" : "custom-select-placeholder"
          }
        >
          <OverflowTooltipLabel
            text={selectedLabel}
            placement="top"
            color="#4A1476"
            maxWidth="260px"
            enableTooltip={hasSelectedValue}
          />
        </span>
        <span className="arrow">
          {open ? <FiChevronUp /> : <FiChevronDown />}
        </span>
      </div>

      {/* DROPDOWN */}
      {open && !disabled && (
        <div className="custom-select-dropdown">
          {/* SEARCH — ONLY IF OPTIONS > 15 */}
{!disableSearch && normalizedOptions.length > SEARCH_THRESHOLD && (
  <div className="custom-select-search-box">
    <input
      type="text"
      placeholder="Search..."
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      autoFocus
      className="custom-select-search-input"
    />
  </div>
)}
          {/* OPTIONS */}
          <div className="custom-select-options">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <div
                  key={opt.value}
                  className={`custom-select-option ${
                    opt.value === value ? "selected" : ""
                  }`}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                    setSearchTerm("");
                  }}
                >
                  <OverflowTooltipLabel
                    text={opt.label}
                    placement="right"
                    color="#4A1476"
                    maxWidth="260px"
                    enableTooltip
                  />
                </div>
              ))
            ) : (
              <div className="custom-select-no-data">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
